import { useEffect, useState } from 'react';
import { onAuthStateChanged, onIdTokenChanged, type Auth, type User } from 'firebase/auth';

/**
 * A small, primitive snapshot of the live Firebase auth state — the ONE place
 * nav surfaces read "is this a permanent (signed-in) account?" from.
 *
 * Why this hook exists (thea-web #<pr>): the v4 new-account door upgrades the
 * anon session IN PLACE — `linkWithPopup` / `linkWithCredential` keep the same
 * uid, flip `isAnonymous` true→false, and MUTATE the existing `User` object.
 * Firebase's `onAuthStateChanged` is SILENT on that transition (it fires only on
 * uid-level sign-in / sign-out); only `onIdTokenChanged` delivers it. A consumer
 * that listens to `onAuthStateChanged` alone therefore never learns the user
 * signed in until the next full page load — the "desktop nav missing after a
 * fresh signup, fixed only by hard refresh" bug. `HeaderAccountMenu` already
 * subscribes to BOTH observers for exactly this reason; this hook packages that
 * pairing so `useDesktopNav`, `MobileTabBarMount`, and `useIsSignedIn` can't
 * drift back onto the single-listener trap.
 *
 * Two deliberate design choices:
 *  - We commit PRIMITIVES (`uid`, `isPermanent`, `emailVerified`, `ready`), never the `User`
 *    object. Because the link mutates the User in place, storing the object and
 *    re-deriving would depend on an incidental re-render; a boolean recomputed
 *    inside each callback is immune to React's same-reference bailout.
 *  - `ready` is FALSE while `currentUser` is null and unbootstrapped, so a
 *    consumer never flashes signed-out chrome before Firebase restores the
 *    session from IndexedDB. It flips true on the first observer callback.
 *
 * Pass `null` to skip all subscription work (used by `useIsSignedIn` when a
 * story/test override short-circuits the live read). Runtime `auth` is the
 * memoized `FirebaseContext` singleton and never swaps live; the render-time
 * instance-awareness below is defensive and keeps the `authInstance` test
 * override honest.
 */

export interface AuthSnapshot {
  uid: string | null;
  /** True only for a permanent (non-anonymous) signed-in user. */
  isPermanent: boolean;
  /** Primitive so verification updates survive Firebase mutating User in place. */
  emailVerified: boolean;
  /** True once the auth state is known (currentUser present at mount, or the
   *  first observer callback landed). False while null + unbootstrapped. */
  ready: boolean;
}

const UNREADY: AuthSnapshot = { uid: null, isPermanent: false, emailVerified: false, ready: false };

const snapshotFor = (user: User | null, ready: boolean): AuthSnapshot => ({
  uid: user?.uid ?? null,
  isPermanent: !!user && !user.isAnonymous,
  emailVerified: !!user?.emailVerified,
  ready,
});

const sameSnap = (a: AuthSnapshot, b: AuthSnapshot): boolean =>
  a.uid === b.uid && a.isPermanent === b.isPermanent && a.ready === b.ready && a.emailVerified === b.emailVerified;

interface SnapState {
  auth: Auth | null;
  snap: AuthSnapshot;
}

const initFor = (auth: Auth | null): SnapState =>
  auth
    ? { auth, snap: snapshotFor(auth.currentUser, auth.currentUser !== null) }
    : { auth: null, snap: UNREADY };

export function useAuthSnapshot(auth: Auth | null): AuthSnapshot {
  const [state, setState] = useState<SnapState>(() => initFor(auth));

  useEffect(() => {
    if (!auth) {
      setState((prev) => (prev.auth === null ? prev : initFor(null)));
      return;
    }
    let cancelled = false;
    // Re-sync from THIS (possibly newly-swapped) instance; deduped so the
    // redundant mount re-sync (the initializer already ran) is a no-op.
    setState((prev) => {
      const next = initFor(auth);
      return prev.auth === auth && sameSnap(prev.snap, next.snap) ? prev : next;
    });
    const onFire = (): void => {
      if (cancelled) return; // ignore a delivery from a disposed subscription
      setState((prev) => {
        if (prev.auth !== auth) return prev; // late delivery from a stale instance
        // Read currentUser FRESH rather than trusting the callback ARGUMENT.
        // Two observers feed this one state and Firebase updates currentUser
        // before notifying either, so the single source of truth can't be
        // clobbered by a stale cross-observer delivery (e.g. an initial
        // onAuthStateChanged(anon) landing after onIdTokenChanged(permanent)).
        // Matches HeaderAccountMenu's read-currentUser-on-render strategy.
        const snap = snapshotFor(auth.currentUser, true); // any callback ⇒ bootstrapped
        return sameSnap(prev.snap, snap) ? prev : { auth, snap }; // dedupe token-refresh churn
      });
    };
    // BOTH observers: onIdTokenChanged catches the in-place anon→permanent link
    // (onAuthStateChanged is silent there); onAuthStateChanged catches cold-load
    // IndexedDB restore + uid swaps. Same pairing HeaderAccountMenu uses.
    const unsubToken = onIdTokenChanged(auth, onFire);
    const unsubAuth = onAuthStateChanged(auth, onFire);
    return () => {
      cancelled = true;
      // Real Firebase always returns an Unsubscribe; guard anyway so cleanup
      // never throws for a stub auth (stories) or a test mock whose impl CRA's
      // resetMocks has stripped to `undefined`. The prior single-listener effects
      // got this tolerance for free by returning the unsub directly.
      if (typeof unsubToken === 'function') unsubToken();
      if (typeof unsubAuth === 'function') unsubAuth();
    };
  }, [auth]);

  if (!auth) return UNREADY;
  // Instance-aware at render: if `auth` changed THIS render (before the effect
  // committed), derive synchronously from the new instance so the previous
  // instance's uid/permanence is never surfaced for a frame.
  if (state.auth !== auth) return snapshotFor(auth.currentUser, auth.currentUser !== null);
  // Reconcile against LIVE currentUser: once bootstrapped, if the committed
  // snapshot lags the live user — currentUser mutated in place, or an incidental
  // re-render (e.g. useAuthInFlux clearing) landed before onFire committed — prefer
  // live, so a re-render can NEVER surface a stale signed-out frame. This is the
  // read-currentUser-on-render robustness the old single-listener hooks had for
  // free (they re-derived from the mutable User ref every render); HeaderAccountMenu
  // relies on the same. No setState here, so no loop — it converges once onFire
  // commits (live === snap). Deduped by value, so the common case returns the
  // stable committed object (no churn).
  if (state.snap.ready) {
    const live = snapshotFor(auth.currentUser, true);
    if (!sameSnap(state.snap, live)) return live;
  }
  return state.snap;
}
